import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';

import { Observable, Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';

import { UserProfileViewModel, MwUserService } from '@core/auth';
import { MwAppConfigService } from '@core/services';
import { Config } from '@core/config';

import { SidebarId } from '../../layout.constants';
import { NotificationBannerService } from '../notification-banner';
import { MwSidebarComponent } from '../sidebar/sidebar.component';
import { MwSidebarService } from '../sidebar/sidebar.service';
import { MwAppToolbarService } from './app-toolbar.service';

@Component({
  selector: 'mw-app-toolbar',
  templateUrl: 'app-toolbar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MwAppToolbarComponent implements OnInit, OnDestroy {
  logo = '';
  title = 'GENERAL.title';
  appModuleSidebarOpened = false;
  profileMenuSidebarOpened = false;
  newsFeedSidebarOpened = false;

  @Input() showToggler = true;
  @Input() showCompactLogo = false;
  @Input() showFullScreenButton = true;
  @Input() clickableTitle = false;

  get showNewsButton(): boolean {
    return this.config?.layout?.toolbar?.showNewsButton ?? true;
  }

  get showModuleButton(): boolean {
    return (
      (this.config?.layout?.toolbar?.showModulesButton ?? true) &&
      this._userProfile?.userId > 0
    );
  }

  @ViewChild('btnModules', { read: ElementRef })
  btnModules: ElementRef;
  @ViewChild('btnProfile', { read: ElementRef })
  btnProfile: ElementRef;
  @ViewChild('btnNews', { read: ElementRef })
  btnNews: ElementRef;

  private modulesSidebar: MwSidebarComponent;
  private profileSidebar: MwSidebarComponent;
  private newsSidebar: MwSidebarComponent;
  private navSidebar: MwSidebarComponent;
  private modulesSidebar$: Observable<MwSidebarComponent>;
  private profileSidebar$: Observable<MwSidebarComponent>;
  private newsSidebar$: Observable<MwSidebarComponent>;
  private navSidebar$: Observable<MwSidebarComponent>;
  private _userProfile: UserProfileViewModel;

  private config: Config;
  private unsubscribeAll$ = new Subject<void>();

  constructor(
    readonly service: MwAppToolbarService,
    readonly notificationBannerService: NotificationBannerService,
    private readonly router: Router,
    private readonly sidebarService: MwSidebarService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly configService: MwAppConfigService,
    private readonly userService: MwUserService
  ) {
    this.subscribeToConfigs();
  }

  ngOnInit(): void {
    this.setupVariables();
    this.subscribeToModuleSidebarOpenChange();
    this.subscribeToProfileSidebarOpenChange();
    this.subscribeToNewsSidebarOpenChange();
    this.subscribeToUserProfile();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }

  onModulesButtonClick(): void {
    if (!this.modulesSidebar) {
      return;
    }

    this.modulesSidebar.open(this.btnModules, true);
  }

  onProfileButtonClick(): void {
    if (!this.profileSidebar) {
      return;
    }

    this.profileSidebar.open(this.btnProfile, true);
  }

  onNewsFeedClick(): void {
    if (!this.newsSidebar) {
      return;
    }
    this.newsSidebar.open(this.btnNews, true);
  }

  onTitleClick(): void {
    this.router.navigate(['/pages']);
  }

  onToggleSidebar(): void {
    const navBarFolded = this.config?.layout?.navbar?.folded === true;
    this.configService.setConfigChunk('layout.navbar.folded', !navBarFolded);
  }

  private setupVariables(): void {
    const setSidebar = this.setSidebar;

    this.modulesSidebar$ = this.getSidebar(SidebarId.AppModules);
    this.profileSidebar$ = this.getSidebar(SidebarId.ProfileMenu);
    this.newsSidebar$ = this.getSidebar(SidebarId.News);
    this.navSidebar$ = this.sidebarService.getSidebar(SidebarId.Navigation);

    setSidebar(this.navSidebar$, (value) => (this.navSidebar = value));
    setSidebar(this.modulesSidebar$, (value) => (this.modulesSidebar = value));
    setSidebar(this.profileSidebar$, (value) => (this.profileSidebar = value));
    setSidebar(this.newsSidebar$, (value) => (this.newsSidebar = value));
  }

  private setSidebar(
    stream$: Observable<MwSidebarComponent>,
    value: (sidebar: MwSidebarComponent) => void
  ): void {
    stream$.subscribe((navSidebar) => value(navSidebar));
  }

  private subscribeToConfigs(): void {
    this.configService
      .getConfig()
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe((config) => {
        this.config = config;
        this.title = config.layout?.toolbar.title || this.title;
        const isNavbarFolded = config.layout.navbar.folded === true;

        if (this.navSidebar?.folded !== isNavbarFolded && this.navSidebar) {
          this.navSidebar.toggleFold();
        }

        this.changeDetectorRef.markForCheck();
      });

    this.notificationBannerService.visibleBannersIds$
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe(() => {
        this.changeDetectorRef.markForCheck();
      });
  }

  private subscribeToNewsSidebarOpenChange(): void {
    this.newsSidebar$
      .pipe(switchMap((sidebar) => sidebar.openChange))
      .subscribe((opened) => {
        this.newsFeedSidebarOpened = opened;
        this.changeDetectorRef.markForCheck();
      });
  }

  private subscribeToProfileSidebarOpenChange(): void {
    this.profileSidebar$
      .pipe(switchMap((sidebar) => sidebar.openChange))
      .subscribe((opened) => {
        this.profileMenuSidebarOpened = opened;
        this.changeDetectorRef.markForCheck();
      });
  }

  private subscribeToModuleSidebarOpenChange(): void {
    this.modulesSidebar$
      .pipe(switchMap((sidebar) => sidebar.openChange))
      .subscribe((opened) => {
        this.appModuleSidebarOpened = opened;
        this.changeDetectorRef.markForCheck();
      });
  }

  private getSidebar(id: string): Observable<MwSidebarComponent> {
    return this.sidebarService.getSidebar(id).pipe(
      takeUntil(this.unsubscribeAll$),
      filter((sidebar) => !!sidebar)
    );
  }

  private subscribeToUserProfile(): void {
    this.userService.userProfile$
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe((profile) => {
        this._userProfile = profile;
        this.changeDetectorRef.detectChanges();
      });
  }
}
